// ApproveScanModal — the admin action that writes an enforced stdio exception
// into a policy tier so a PASSED (non-dry-run) scanned MCP server may launch
// (POST /api/v1/scans/{id}/approve → policy.AddStdioException). Built in the
// ProvisionSshSandboxModal idiom: Form → Review → Result, because writing a
// policy exception is a high-consequence action that deserves a review step.
//
// The caller only opens this for an approvable scan (the drawer gates the
// trigger on admin + done + passed + !dry_run, matching the handler's 422s);
// the modal still surfaces a server 422/400/404 honestly if one slips through.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Select, Input, Button, Alert, Tag, message } from "antd";
import { KeyRound, ShieldCheck, Check, AlertTriangle } from "lucide-react";
import { useApi } from "@/lib/api/client";
import { scansApi, shortHash, type ScanResult, type ApproveResult } from "@/lib/api/scans";

const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

// The launch command the exception whitelists — Phase A the admin supplies it
// (the scan does not capture it) and vouches for it.
const COMMAND_PLACEHOLDER = "npx -y @modelcontextprotocol/server-github";

interface TierOption {
  id: string;
  label: string; // tier_name || name
  isTier: boolean;
}

export default function ApproveScanModal({
  open,
  onClose,
  result,
  onApproved,
}: {
  open: boolean;
  onClose: () => void;
  result: ScanResult;
  onApproved?: () => void;
}) {
  const api = useApi();
  const [tiers, setTiers] = useState<TierOption[]>([]);
  const [tiersErr, setTiersErr] = useState<string | null>(null);
  const [policyId, setPolicyId] = useState<string>();
  const [serverName, setServerName] = useState("");
  const [command, setCommand] = useState("");
  const [justification, setJustification] = useState("");
  const [review, setReview] = useState(false);

  // A finished, real (non-dry-run) scan that did NOT pass can still be approved,
  // but only as an explicit admin override — a risk acceptance the server records
  // as mcp_scan.override. The dry-run case never reaches an approvable state.
  const isOverride = result.status === "done" && !result.dry_run && !result.passed;
  const MIN_JUSTIFICATION = 10;
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ApproveResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const loadPolicies = useCallback(async () => {
    setTiersErr(null);
    try {
      const opts: TierOption[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < 20; page++) {
        const res = await api.GET("/policies", { params: { query: { cursor, limit: 100 } } });
        if (res.error) throw new Error("Could not load policies");
        for (const p of res.data?.items ?? []) {
          opts.push({ id: p.id, label: p.tier_name || p.name, isTier: Boolean(p.tier_name) });
        }
        cursor = res.data?.next_cursor;
        if (!cursor) break;
      }
      // Tiers first (the real enforcement targets), then the rest.
      opts.sort((a, b) => (a.isTier === b.isTier ? 0 : a.isTier ? -1 : 1));
      setTiers(opts);
    } catch (e) {
      setTiersErr(e instanceof Error ? e.message : "Could not load policies");
    }
  }, [api]);

  useEffect(() => {
    if (!open) return;
    setPolicyId(undefined);
    setServerName(result.server || "");
    setCommand("");
    setJustification("");
    setReview(false);
    setDone(null);
    setErr(null);
    void loadPolicies();
    // loadPolicies is stable per api; result.server is the prefill source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, result.server]);

  const selectedTier = useMemo(
    () => tiers.find((t) => t.id === policyId),
    [tiers, policyId],
  );
  const tierLabel = selectedTier?.label ?? "the selected tier";

  const canReview =
    !!policyId &&
    serverName.trim().length > 0 &&
    command.trim().length > 0 &&
    (!isOverride || justification.trim().length >= MIN_JUSTIFICATION);

  const submit = async () => {
    if (!policyId) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await scansApi.approve(result.scan_id, {
        policy_id: policyId,
        server_name: serverName.trim(),
        command: command.trim(),
        ...(isOverride ? { override: true, justification: justification.trim() } : {}),
      });
      setDone(res);
      message.success(isOverride ? "Override recorded — exception written" : "Scoped exception written");
      onApproved?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Approve failed");
      setReview(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={640}
      footer={null}
      destroyOnHidden
      title={
        <span className="inline-flex items-center gap-2">
          {isOverride ? (
            <>
              <AlertTriangle size={17} className="text-warning" /> Override failed scan
            </>
          ) : (
            <>
              <KeyRound size={17} className="text-accent" /> Grant scoped exception
            </>
          )}
        </span>
      }
    >
      {err && <Alert type="error" showIcon className="mb-3" message={err} />}

      {done ? (
        /* result step */
        <div>
          <Alert
            type="success"
            showIcon
            className="mb-3"
            message="Scoped exception written"
            description={`Policy ${done.policy_id} is now at version ${done.new_version}. The server may launch under ${tierLabel}, bound to the scanned artifact (${shortHash(result.content_hash)}).`}
          />
          <p className="mt-3 text-xs text-text-muted">
            The exception is audited and revocable — it is scoped to this exact command and tier, never
            a blanket allow.
          </p>
          <div className="mt-4 flex justify-end">
            <Button type="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : review ? (
        /* review step */
        <div>
          <p className="mb-3 text-sm text-text-secondary">
            Review before writing — this adds an enforced stdio exception to the policy tier.
          </p>
          <div className="rounded-xl border border-border bg-surface-elev px-4">
            <Row label="Tier" value={tierLabel} />
            <Row label="Server" value={serverName.trim()} mono />
            <Row label="Command" value={command.trim()} mono />
            <Row label="Artifact" value={shortHash(result.content_hash)} mono />
          </div>

          {isOverride && (
            <Alert
              type="warning"
              showIcon
              icon={<AlertTriangle size={16} />}
              className="mt-3"
              message={`Override — approving a scan that did not pass${
                result.worst_severity ? ` (worst: ${result.worst_severity.toUpperCase()})` : ""
              }`}
              description={
                <>
                  Recorded as <code style={{ fontFamily: MONO }}>mcp_scan.override</code> against your
                  identity. Justification: <i>{justification.trim()}</i>
                </>
              }
            />
          )}

          {/* message_threshold caveat — feedback_mcp-message-threshold-gotcha.
              The exception admits the server, but the tier's MCP threshold has to
              be workable or the launch is admitted-but-broken. */}
          <Alert
            type="warning"
            showIcon
            className="mt-3"
            message="Check this tier's MCP message_threshold"
            description={
              <>
                Writing this exception lets the server launch under <b>{tierLabel}</b>. That tier's MCP{" "}
                <code style={{ fontFamily: MONO }}>message_threshold</code> must be workable — safe = 1 MiB
                (<code style={{ fontFamily: MONO }}>MCPMessageThresholdSafeBytes</code>,{" "}
                <code style={{ fontFamily: MONO }}>uc/uc.go:1172</code>). Left at the 256-byte cap, the
                exception admits the server but mangles the MCP{" "}
                <code style={{ fontFamily: MONO }}>initialize</code> and it won't work.
              </>
            }
          />

          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={() => setReview(false)} disabled={busy}>
              Back
            </Button>
            <Button
              type="primary"
              danger={isOverride}
              icon={isOverride ? <AlertTriangle size={15} /> : <Check size={15} />}
              loading={busy}
              onClick={submit}
            >
              {isOverride ? "Override & write exception" : "Write exception"}
            </Button>
          </div>
        </div>
      ) : (
        /* form step */
        <div>
          {tiersErr && <Alert type="error" showIcon className="mb-3" message={tiersErr} />}
          {isOverride && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={`This scan did not pass${
                result.worst_severity ? ` (worst finding: ${result.worst_severity.toUpperCase()})` : ""
              }`}
              description="Approving is an override — an explicit risk acceptance recorded in the audit log as mcp_scan.override, tied to you and your justification. The exception stays scoped to this exact command and tier, and remains revocable."
            />
          )}
          <Form layout="vertical">
            <Form.Item
              label="Policy tier"
              required
              tooltip="The tier whose policy gains the stdio exception. Tiers are the always-deployed enforcement targets; a normal policy works too."
            >
              <Select
                value={policyId}
                onChange={setPolicyId}
                placeholder={tiers.length ? "Select a tier" : "No policies loaded"}
                notFoundContent="No policies loaded"
                options={tiers.map((t) => ({
                  value: t.id,
                  label: (
                    <span className="inline-flex items-center gap-2">
                      <Tag color="purple" style={{ fontFamily: MONO, margin: 0 }}>
                        {t.label}
                      </Tag>
                      {!t.isTier && <span className="text-xs text-text-muted">policy</span>}
                    </span>
                  ),
                }))}
              />
            </Form.Item>
            <Form.Item label="Server name" required tooltip="The MCP server identifier the exception is written for. Prefilled from the scan.">
              <Input value={serverName} onChange={(e) => setServerName(e.target.value)} placeholder="github" />
            </Form.Item>
            <Form.Item
              label="Launch command"
              required
              tooltip="The exact command the exception whitelists. Phase A: you supply and vouch for it — the scan does not capture it."
            >
              <Input.TextArea
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                rows={2}
                placeholder={COMMAND_PLACEHOLDER}
                style={{ fontFamily: MONO, fontSize: 12 }}
              />
            </Form.Item>
            {isOverride && (
              <Form.Item
                label="Justification"
                required
                tooltip="Why you are accepting the risk of the failing findings. Recorded in the audit log with the override."
                help={
                  justification.trim().length > 0 && justification.trim().length < MIN_JUSTIFICATION
                    ? `At least ${MIN_JUSTIFICATION} characters — state the reason.`
                    : undefined
                }
                validateStatus={
                  justification.trim().length > 0 && justification.trim().length < MIN_JUSTIFICATION
                    ? "warning"
                    : undefined
                }
              >
                <Input.TextArea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={2}
                  placeholder="e.g. Script-injection finding is in a test fixture, not the server path; risk accepted for the pilot."
                />
              </Form.Item>
            )}
          </Form>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<ShieldCheck size={15} />}
              disabled={!canReview}
              onClick={() => setReview(true)}
            >
              Review
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );

  function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
    return (
      <div className="flex items-center justify-between gap-4 border-b border-border py-2 last:border-b-0">
        <span className="text-xs text-text-muted">{label}</span>
        <span
          className={`min-w-0 break-all text-right text-[13px] text-text-primary ${mono ? "" : "font-medium"}`}
          style={mono ? { fontFamily: MONO } : undefined}
        >
          {value}
        </span>
      </div>
    );
  }
}
